import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  compositeScore,
  CRITERIA_GROUPS,
  MOCK_EVALUATORS,
  MOCK_SCORES,
  CATEGORY_NAMES,
  type Candidate,
} from "./mockSelectionData";
import type { Bucket } from "@/pages/hub/HubSelectionDemo";

interface Props {
  candidate: Candidate | null;
  bucket: Bucket;
  rank: number;
  onClose: () => void;
}

const BUCKET_LABEL: Record<Bucket, string> = {
  selected: "Selected",
  reserves: "Reserves",
  not_selected: "Not Selected",
};

const BUCKET_BADGE: Record<Bucket, string> = {
  selected: "bg-green-100 text-green-800 border-green-300",
  reserves: "bg-blue-100 text-blue-800 border-blue-300",
  not_selected: "bg-gray-100 text-gray-700 border-gray-300",
};

function categoryAvgForCandidate(candidateId: number, category: string): number {
  const groups = CRITERIA_GROUPS.filter((g) => g.category === category);
  const keys = groups.flatMap((g) => g.criteria.map((c) => c.key));
  const allVals = MOCK_EVALUATORS.flatMap((ev) =>
    keys.map((k) => MOCK_SCORES[candidateId][ev][k])
  );
  return allVals.reduce((a, b) => a + b, 0) / allVals.length;
}

function criterionAvg(candidateId: number, key: string): number {
  const vals = MOCK_EVALUATORS.map((ev) => MOCK_SCORES[candidateId][ev][key]);
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

export function SelectionReportModal({ candidate, bucket, rank, onClose }: Props) {
  if (!candidate) return null;

  const composite = compositeScore(candidate.id);

  // Category averages ranked
  const catAvgs = CATEGORY_NAMES.map((cat) => ({
    cat,
    avg: categoryAvgForCandidate(candidate.id, cat),
  })).sort((a, b) => b.avg - a.avg);

  const topCat = catAvgs[0].cat;
  const bottomCat = catAvgs[catAvgs.length - 1].cat;

  // Top 3 and bottom 2 criteria overall
  const allCriteria = CRITERIA_GROUPS.flatMap((g) =>
    g.criteria.map((c) => ({ label: c.label, avg: criterionAvg(candidate.id, c.key) }))
  ).sort((a, b) => b.avg - a.avg);
  const top3 = allCriteria.slice(0, 3);
  const bottom2 = allCriteria.slice(-2);

  // Draft parent rationale
  let rationale = "";
  if (bucket === "selected") {
    rationale = `Selected based on consistently strong performance across ${topCat}. Composite score of ${composite.toFixed(2)} ranked ${rank}${rank === 1 ? "st" : rank === 2 ? "nd" : rank === 3 ? "rd" : "th"} in the pool.`;
  } else if (bucket === "reserves") {
    rationale = `Placed in reserve based on ${topCat} strength. Areas to develop before next phase: ${bottomCat}.`;
  } else {
    rationale = `Not selected for this phase. Demonstrated strength in ${topCat}. Focus areas for continued development: ${bottomCat}. We encourage continued participation.`;
  }

  return (
    <Dialog open={!!candidate} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="max-w-lg w-full max-h-[90vh] overflow-y-auto"
        data-testid="selection-report-modal"
      >
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="text-lg font-bold text-gray-900">
                {candidate.name}
              </DialogTitle>
              <p className="text-sm text-gray-500 mt-0.5">
                {candidate.ageGroup} · {candidate.position}
              </p>
            </div>
            <Badge className={`border text-xs shrink-0 mt-1 ${BUCKET_BADGE[bucket]}`}>
              {BUCKET_LABEL[bucket]}
            </Badge>
          </div>
        </DialogHeader>

        {/* Composite score */}
        <div className="flex items-center gap-3 py-3 border-y border-gray-100">
          <div className="text-3xl font-bold text-gray-800">{composite.toFixed(2)}</div>
          <div className="text-sm text-gray-500">
            Composite score<br />
            <span className="text-gray-400 text-xs">avg across all 23 criteria · 3 selectors</span>
          </div>
        </div>

        {/* Category breakdown */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Category Breakdown
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {catAvgs.map(({ cat, avg }) => (
              <div
                key={cat}
                className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2"
              >
                <span className="text-sm text-gray-700">{cat}</span>
                <span className="text-sm font-semibold text-gray-800">{avg.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top 3 criteria */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Highest Scoring Criteria
          </p>
          <div className="space-y-1">
            {top3.map((c) => (
              <div key={c.label} className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-green-50">
                <span className="text-sm text-gray-700">{c.label}</span>
                <span className="text-sm font-semibold text-green-700">{c.avg.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom 2 criteria */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Development Areas
          </p>
          <div className="space-y-1">
            {bottom2.map((c) => (
              <div key={c.label} className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-amber-50">
                <span className="text-sm text-gray-700">{c.label}</span>
                <span className="text-sm font-semibold text-amber-700">{c.avg.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Selector scores */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Selector Averages
          </p>
          <div className="flex gap-3">
            {MOCK_EVALUATORS.map((ev) => {
              const allVals = Object.values(MOCK_SCORES[candidate.id][ev]);
              const avg = allVals.reduce((a, b) => a + b, 0) / allVals.length;
              return (
                <div key={ev} className="flex-1 text-center rounded-lg bg-gray-50 py-2 px-1">
                  <p className="text-xs text-gray-500">{ev}</p>
                  <p className="text-lg font-bold text-gray-800">{avg.toFixed(2)}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Parent rationale */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Draft Parent Communication
          </p>
          <p className="text-sm text-gray-700 leading-relaxed">{rationale}</p>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => window.print()}
            data-testid="btn-download-report"
          >
            Download PDF
          </Button>
          <DialogClose asChild>
            <Button
              className="flex-1 bg-[#1E2761] hover:bg-[#2d3a8c] text-white"
              data-testid="btn-close-report"
            >
              Close
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}
